import { gql, useQuery } from "@apollo/client";
import {
  TargetRow_compCycleData as CompCycleData,
  GetLazyReportingTargetTierData,
  TargetRow_employee,
} from "../../../../__generated__/graphql";
import { CompCycleDisplay } from "../../../../components/CompCycle/types";
import { useCurrencies } from "../../../../components/CurrenciesContext";
import { IndentationCells } from "../../Budgets/Table/Connectors";
import { SkeletonRow } from "../../Budgets/Table/SkeletonRow";
import { ReportingTargetTier } from "./ReportingTargetTier";

type Props = {
  employee: TargetRow_employee;
  organizationName: string;
  varianceDisplay: CompCycleDisplay;
  compCycleData: CompCycleData;
  indentation: IndentationCells;
};
export function LazyReportingTargetTier({
  employee,
  organizationName,
  varianceDisplay,
  compCycleData,
  indentation,
}: Props): JSX.Element {
  const { defaultCurrencyCode } = useCurrencies();
  const { loading, data } = useQuery<GetLazyReportingTargetTierData>(
    LazyReportingTargetTier.query(),
    {
      variables: {
        compCycleId: compCycleData.id,
        employeeId: employee.id,
        currencyCode: defaultCurrencyCode,
      },
      pollInterval: 10_000,
    }
  );

  if (loading) {
    return <SkeletonRow subComponents={3} indentation={indentation} />;
  }

  if (!data || !data.compCycle) {
    return (
      <tr>
        <td>Error</td>
      </tr>
    );
  }

  return (
    <ReportingTargetTier
      rootBudget={data.compCycleBudget}
      manager={employee}
      reports={data.employees}
      organizationName={organizationName}
      compCycleData={compCycleData}
      varianceDisplay={varianceDisplay}
      indentation={indentation}
    />
  );
}

LazyReportingTargetTier.query = () => gql`
  ${ReportingTargetTier.fragments.reports}
  ${ReportingTargetTier.fragments.compCycleBudget}
  ${ReportingTargetTier.fragments.compCycleData}
  query GetLazyReportingTargetTierData(
    $compCycleId: Int!
    $employeeId: Int!
    $currencyCode: CurrencyCode!
  ) {
    compCycleBudget(compCycleId: $compCycleId, employeeId: $employeeId) {
      compCycleId
      employeeId
      ...ReportingTargetTier_compCycleBudget
    }
    employees(managerId: $employeeId) {
      ...ReportingTargetTier_reports
    }
    compCycle(id: $compCycleId) {
      id
      ...ReportingTargetTier_compCycleData
    }
  }
`;
